/**
 * Courses Analytics Panel
 *
 * Renders course enrollments, completions, revenue, and top courses for
 * the selected time range. Reads from the admin /api/admin/analytics/courses
 * endpoint via useAdminCourses.
 */
import {
  BookOpen,
  CheckCircle2,
  DollarSign,
  GraduationCap,
  Loader2,
  ShoppingCart,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  formatCents,
  useAdminCourses,
  type AnalyticsRange,
} from "@/hooks/useAdminAnalytics";

interface Props {
  range: AnalyticsRange;
}

export function CoursesPanel({ range }: Props) {
  const { data, isLoading, error } = useAdminCourses(range);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-[#B87333]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-slate-600">
          Unable to load course analytics.
        </CardContent>
      </Card>
    );
  }

  const stats = [
    {
      label: "New Enrollments",
      value: data.new_enrollments,
      icon: <GraduationCap className="h-5 w-5" />,
    },
    {
      label: "Course Completions",
      value: data.course_completions,
      icon: <CheckCircle2 className="h-5 w-5" />,
    },
    {
      label: "Lesson Completions",
      value: data.lesson_completions,
      icon: <BookOpen className="h-5 w-5" />,
    },
    {
      label: "Paid Purchases",
      value: data.purchase_count,
      icon: <ShoppingCart className="h-5 w-5" />,
    },
  ];

  const dailyData = data.daily_enrollments.map((d) => ({
    date: d.date.slice(5),
    enrollments: d.enrollments,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="border-2">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                {s.label}
              </CardTitle>
              <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
                {s.icon}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                {s.value.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-[#B87333]" /> Revenue
          </CardTitle>
          <CardDescription>
            Completed paid course purchases in the selected window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.revenue.length === 0 ? (
            <p className="text-sm text-slate-500">
              No completed purchases in this range.
            </p>
          ) : (
            <div className="space-y-2">
              {data.revenue.map((r) => (
                <div
                  key={r.currency}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <span className="font-semibold text-slate-800 uppercase">
                    {r.currency}
                  </span>
                  <div className="text-right">
                    <p className="text-xl font-bold text-[#B87333]">
                      {formatCents(r.amount_cents, r.currency)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {r.purchases} purchase{r.purchases === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Enrollments Over Time</CardTitle>
          <CardDescription>Daily new enrollments.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="enrollments"
                  stroke="#B87333"
                  fill="#B87333"
                  fillOpacity={0.2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top Courses</CardTitle>
          <CardDescription>
            Courses with the most new enrollments in this window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.top_courses.length === 0 ? (
            <p className="text-sm text-slate-500">
              No enrollments in this range.
            </p>
          ) : (
            <div className="space-y-2">
              {data.top_courses.map((c) => (
                <div
                  key={c.course_id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:border-[#B87333] transition-colors"
                >
                  <span className="font-medium text-slate-800">{c.title}</span>
                  <span className="text-lg font-bold text-[#B87333]">
                    {c.enrollments.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CoursesPanel;
